import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../i18n';

/** Ou se trouve la ligne pressee, dans le repere de l'ecran. */
export interface RowAnchor {
  top: number;
  bottom: number;
  left: number;
  width: number;
}

/** Air laisse entre la ligne et le menu, et entre le menu et les bords. */
const GAP = 8;
const EDGE = 14;

/**
 * Petit menu qui surgit contre la ligne qu'on tient.
 *
 * Une feuille qui monte du bas convient a un ecran entier ; sur une liste
 * ou chaque ligne a ses propres actions, elle oblige a redescendre le
 * regard et a relire quel element on visait. Le menu se pose donc contre
 * la ligne, et le titre du fil reste sous les yeux.
 *
 * Il s'accroche a l'ecran et non a la zone defilante : pose dans la
 * liste, il s'ouvrirait sous le pli des que la ligne est en bas.
 */
export function RowMenu({
  anchor,
  onClose,
  children,
}: {
  /** `null` ferme le menu. */
  anchor: RowAnchor | null;
  onClose: () => void;
  children: ReactNode;
}) {
  const card = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState<{ top: number; left: number } | null>(null);

  /*
    On mesure avant de peindre : le menu se pose sous la ligne s'il y tient,
    au-dessus sinon. Sans cette mesure, une conversation en bas de liste
    aurait ouvert son menu hors de l'ecran.
  */
  useLayoutEffect(() => {
    const node = card.current;
    if (!anchor || !node) {
      setPlaced(null);
      return;
    }

    const screen = node.parentElement;
    if (!screen) return;

    /*
      La barre d'onglets flotte au-dessus du bas de l'ecran : elle ne
      cache plus les elements poses par-dessus, mais s'ouvrir dessous
      reviendrait quand meme a viser une cible a moitie couverte.
    */
    const bar = document.querySelector('.tabbar')?.getBoundingClientRect().height ?? 0;
    const room = screen.clientHeight - bar;
    const height = node.offsetHeight;
    const below = anchor.bottom + GAP;

    setPlaced({
      top: below + height + EDGE <= room ? below : Math.max(EDGE, anchor.top - GAP - height),
      left: Math.max(EDGE, Math.min(anchor.left, screen.clientWidth - node.offsetWidth - EDGE)),
    });
  }, [anchor]);

  useEffect(() => {
    if (!anchor) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Rien ne defile derriere : le menu vise une ligne, elle doit rester ou
    // elle est.
    document.documentElement.classList.add('sheet-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.documentElement.classList.remove('sheet-open');
    };
  }, [anchor, onClose]);

  if (!anchor) return null;

  return (
    <>
      <button className="row-menu__scrim" aria-label={t.commun.fermer} onClick={onClose} />
      <div
        ref={card}
        className="row-menu"
        role="menu"
        /* Invisible tant qu'il n'est pas place : sinon il apparait en haut
           a gauche le temps d'une image. */
        style={placed ? { top: placed.top, left: placed.left } : { opacity: 0 }}
      >
        {children}
      </div>
    </>
  );
}

/** Une entree du menu : un libelle, une icone a gauche. */
export function RowMenuItem({
  icon,
  children,
  onClick,
  tone = 'default',
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  /** `danger` pour ce qui ne se defait pas. */
  tone?: 'default' | 'danger';
}) {
  return (
    <button className={`row-menu__item row-menu__item--${tone}`} role="menuitem" onClick={onClick}>
      {icon}
      <span className="row-menu__label">{children}</span>
    </button>
  );
}
